import type { DeclineDataPoint } from './declineCalculations';
import { fitExponentialDecline, fitHyperbolicDecline, fitHarmonicDecline, fitSepd, fitDuong, predictDeclineRate, predictSepdRate, predictDuongRate } from './declineCalculations';

export type SensitivityDeclineModel = 'exponential' | 'hyperbolic' | 'harmonic' | 'sepd' | 'duong' | 'segmented';

export interface SensitivityModelResult {
  key: SensitivityDeclineModel;
  label: string;
  r2: number;
  rmse: number;
  futureCumulative: number;
  finalRate: number;
}

export interface SensitivityCurvePoint { t: number; q: number; }
export interface SensitivityModelCurve { key: SensitivityDeclineModel; label: string; points: SensitivityCurvePoint[]; }

export function buildSensitivityModelResults(data: DeclineDataPoint[], lastT: number, forecastEndT: number, terminalAnnualEffectivePct = 5): SensitivityModelResult[] {
  if (data.length < 3 || forecastEndT <= lastT) return [];

  const predictors = buildPredictors(data, terminalAnnualEffectivePct);
  return predictors.map((item) => ({
    key: item.key,
    label: item.label,
    r2: item.r2,
    rmse: item.rmse,
    futureCumulative: integratePrediction(item.predict, lastT, forecastEndT),
    finalRate: Math.max(0, item.predict(forecastEndT)),
  }));
}

export function buildSensitivityModelCurves(data: DeclineDataPoint[], lastT: number, forecastEndT: number, terminalAnnualEffectivePct = 5, pointCount = 220): SensitivityModelCurve[] {
  if (data.length < 3 || forecastEndT <= lastT) return [];

  const predictors = buildPredictors(data, terminalAnnualEffectivePct);
  const count = Math.max(50, Math.min(500, pointCount));

  return predictors.map((item) => {
    const points: SensitivityCurvePoint[] = [];
    for (let i = 0; i <= count; i += 1) {
      const t = lastT + (forecastEndT - lastT) * i / count;
      points.push({ t, q: Math.max(0, item.predict(t)) });
    }
    return { key: item.key, label: item.label, points };
  });
}

function buildPredictors(data: DeclineDataPoint[], terminalAnnualEffectivePct: number) {
  const predictors: { key: SensitivityDeclineModel; label: string; r2: number; rmse: number; predict: (t: number) => number }[] = [];

  const exponential = fitExponentialDecline(data);
  if (exponential) predictors.push({ key: 'exponential', label: '指数递减', r2: exponential.r2, rmse: exponential.rmse, predict: (t) => predictDeclineRate(exponential, t) });

  const hyperbolic = fitHyperbolicDecline(data);
  if (hyperbolic) predictors.push({ key: 'hyperbolic', label: '双曲递减', r2: hyperbolic.r2, rmse: hyperbolic.rmse, predict: (t) => predictDeclineRate(hyperbolic, t) });

  const harmonic = fitHarmonicDecline(data);
  if (harmonic) predictors.push({ key: 'harmonic', label: '调和递减', r2: harmonic.r2, rmse: harmonic.rmse, predict: (t) => predictDeclineRate(harmonic, t) });

  const sepd = fitSepd(data);
  if (sepd) predictors.push({ key: 'sepd', label: 'SEPD', r2: sepd.r2, rmse: sepd.rmse, predict: (t) => predictSepdRate(sepd, t) });

  const duong = fitDuong(data);
  if (duong) predictors.push({ key: 'duong', label: 'Duong', r2: duong.r2, rmse: duong.rmse, predict: (t) => predictDuongRate(duong, t) });

  if (hyperbolic) {
    const effective = Math.min(99, Math.max(0.01, terminalAnnualEffectivePct)) / 100;
    const terminalNominal = -Math.log(1 - effective) / 365;
    const transitionT = hyperbolic.b > 0 ? Math.max(0, (hyperbolic.di / terminalNominal - 1) / (hyperbolic.b * hyperbolic.di)) : 0;
    const transitionQ = predictDeclineRate(hyperbolic, transitionT);
    const predict = (t: number) => t <= transitionT ? predictDeclineRate(hyperbolic, t) : transitionQ * Math.exp(-terminalNominal * (t - transitionT));
    predictors.push({ key: 'segmented', label: '双曲→指数分段', r2: hyperbolic.r2, rmse: hyperbolic.rmse, predict });
  }

  return predictors;
}

function integratePrediction(predict: (t: number) => number, startT: number, endT: number) {
  const steps = Math.max(100, Math.min(2000, Math.ceil(endT - startT)));
  const dt = (endT - startT) / steps;
  let total = 0;

  for (let i = 0; i < steps; i += 1) {
    const t0 = startT + i * dt;
    const t1 = t0 + dt;
    total += 0.5 * (Math.max(0, predict(t0)) + Math.max(0, predict(t1))) * dt;
  }

  return total;
}